import React, { useState, useEffect, useRef } from "react";
import Swal from "sweetalert2";
import RecordPosition from "../RecordPosition/RecordPosition";
import { similarInteriorDesignHook } from "./useHook";

const SimilarInteriorDesignModal = ({ isOpen, onClose, designId }) => {
  const {
    addSimilarInteriorDesign,
    deleteSimilarInteriorDesign,
    getSimilarInteriorDesignData,
    getSimilarInteriorDesignExpectSelected,
    updateSimilarInteriorDesignPosition,
  } = similarInteriorDesignHook();

  const [isPositionModalOpen, setIsPositionModalOpen] = useState(false);
  const [selectedForPosition, setSelectedForPosition] = useState(null);

  const [designData, setDesignData] = useState([]);
  const [selectedSimilarIds, setSelectedSimilarIds] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [getInteriorDesign, setGetInteriorDesign] = useState([]);
  const dropdownRef = useRef(null);

  useEffect(() => {
    getSimilarInteriorDesignExpectSelected(setGetInteriorDesign, designId);
  }, [designId]);

  useEffect(() => {
    getSimilarInteriorDesignData(setDesignData, designId);
  }, [isOpen]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleToggleSimilar = (id) => {
    if (selectedSimilarIds.includes(id)) {
      setSelectedSimilarIds((prev) => prev.filter((pid) => pid !== id));
    } else {
      setSelectedSimilarIds((prev) => [...prev, id]);
    }
  };

  const handleRemoveChip = (id) => {
    setSelectedSimilarIds((prev) => prev.filter((pid) => pid !== id));
  };

  const handleSubmitSimilarDesign = async () => {
    const payload = { designId, similarIds: selectedSimilarIds };
    await addSimilarInteriorDesign(payload);
    Swal.fire({
      title: "Success",
      text: "Similar interior design added successfully",
      icon: "success",
      confirmButtonText: "OK",
    }).then(() => {
      onClose();
    });
  };

  const handleDeleteSimilarDesign = async (id) => {
    await deleteSimilarInteriorDesign(id);
    Swal.fire({
      title: "Deleted",
      text: "Similar interior design has been deleted.",
      icon: "success",
      confirmButtonText: "OK",
    }).then(() => {
      getSimilarInteriorDesignExpectSelected(setGetInteriorDesign, designId);
      getSimilarInteriorDesignData(setDesignData, designId);
    });
  };

  const handlePositionClick = (relation) => {
    const junction = relation.similar_interior_designs;
    if (!junction?.id) return;
    const currentPosition = junction.position ?? 0;
    setSelectedForPosition({ recordId: junction.id, currentPosition });
    setIsPositionModalOpen(true);
  };

  const handleUpdatePosition = async (recordId, position) => {
    await updateSimilarInteriorDesignPosition(recordId, position);
    getSimilarInteriorDesignData(setDesignData, designId);
  };

  const closePositionModal = () => {
    setIsPositionModalOpen(false);
    setSelectedForPosition(null);
  };

  const mainProduct =
    designData && designData.length > 0
      ? designData.find((p) => p.id === designId)
      : null;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-800 bg-opacity-50">
      <div className="bg-white p-4 rounded shadow-lg w-96">
        <h2 className="text-xl font-bold mb-4">Add/Delete Similar Interior Designs</h2>

        {/* Linked Products Section */}
        <div className="mb-4">
          <h3 className="text-md font-bold">Similar Interior Designs</h3>
          <ul className="mt-2">
            {Array.isArray(designData) && designData.length > 0 ? (
              designData.map((relation) => (
                <li
                  key={relation.id}
                  className="mb-2 flex justify-between items-center"
                >
                  <span className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handlePositionClick(relation)}
                      className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-medium min-w-[2rem]"
                    >
                      {relation.similar_interior_designs?.position ?? "—"}
                    </button>
                    <span>{relation.title || "Unknown Interior Design"}</span>
                  </span>
                  <button
                    className="text-red-500 ml-2"
                    onClick={() =>
                      handleDeleteSimilarDesign(relation.similar_interior_designs?.id)
                    }
                  >
                    &times;
                  </button>
                </li>
              ))
            ) : (
              <p>No similar interior designs available</p>
            )}
          </ul>
        </div>

        <div className="mb-4 flex flex-wrap gap-2">
          {selectedSimilarIds?.map((id) => {
            const product = getInteriorDesign.find((p) => p.id === id);
            return (
              <span
                key={id}
                className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-700"
              >
                {product?.title || "Unknown"}
                <button
                  className="ml-2 text-blue-700 hover:text-blue-900"
                  onClick={() => handleRemoveChip(id)}
                >
                  &times;
                </button>
              </span>
            );
          })}
        </div>

        <div className="relative" ref={dropdownRef}>
          <button
            className="w-full border border-gray-300 bg-white px-4 py-2 rounded-md text-left focus:outline-none"
            onClick={() => setIsDropdownOpen((prev) => !prev)}
          >
            {selectedSimilarIds.length
              ? "Add more interior designs..."
              : "Select similar interior designs..."}
          </button>

          {isDropdownOpen &&
            Array.isArray(getInteriorDesign) &&
            getInteriorDesign.length > 0 && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow max-h-48 overflow-y-auto">
                {getInteriorDesign.map((prod) => (
                  <div
                    key={prod.id}
                    className={`px-4 py-2 flex items-center ${Array.isArray(designData) &&
                        designData.some((item) => item.similar_interior_design_id === prod.id)
                        ? "bg-gray-200 cursor-not-allowed"
                        : "cursor-pointer hover:bg-gray-100"
                      }`}
                    onClick={() => {
                      if (!(Array.isArray(designData) &&
                        designData.some((item) => item.similar_interior_design_id === prod.id))) {
                        handleToggleSimilar(prod.id);
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={
                        selectedSimilarIds.includes(prod.id) ||
                        (Array.isArray(designData) &&
                          designData.some((item) => item.similar_interior_design_id === prod.id))
                      }
                      disabled={
                        Array.isArray(designData) &&
                        designData.some((item) => item.similar_interior_design_id === prod.id)
                      }
                      readOnly
                      className="mr-2"
                    />

                    <span className={
                      Array.isArray(designData) &&
                        designData.some((item) => item.similar_interior_design_id === prod.id)
                        ? "text-gray-500"
                        : ""
                    }>{prod.title || "Unknown Interior Design"}</span>
                  </div>
                ))}
              </div>
            )}
        </div>

        <div className="mt-4 flex justify-end space-x-2">
          <button
            className="bg-gray-400 text-white py-2 px-4 rounded"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded"
            onClick={handleSubmitSimilarDesign}
          >
            Save
          </button>
        </div>
      </div>

      {isPositionModalOpen && selectedForPosition && (
        <RecordPosition
          isOpen={isPositionModalOpen}
          onClose={closePositionModal}
          currentPosition={selectedForPosition.currentPosition}
          recordId={selectedForPosition.recordId}
          onUpdatePosition={handleUpdatePosition}
          moduleName="Similar Interior Design"
        />
      )}
    </div>
  );
};

export default SimilarInteriorDesignModal;
